(ns core.front.comm
  "Send HTTP requests to the backend

   :comm/command effect sends a request

   :comm/command-monitored event sends a request and allows tracking the status with subscriptions
                           :comm/command-processing? and :comm/current-processing-commands

   Usage:
   {:comm/command {;; command name is the keyword defined in the backend, will be mapped to an API URL
                   :command :foo/bar

                   ;; method is the HTTP verb, can be only :get :post
                   :method  :post

                   ;; data is encoded in query params for :get, transit body for :post
                   :data    {:foo \"bar\"}

                   ;; an optional :notify key can define messages to show on result
                   :notify  {:success               :my.translation/key
                             :not-modified          \"my non-translated text\"
                             :bad-request           ...
                             :unauthorized          ...
                             :forbidden             ...
                             :not-found             ...
                             :request-timeout       ...
                             :gone                  ...
                             :precondition-failed   ...
                             :enhance-your-calm     ...
                             :locked                ...
                             :internal-server-error ...
                             :bad-gateway           ...}

                   ;; handle the result with a re-frame event, params are [_ resp-code data request-id]
                   ;; where resp-code is http status code, data is deserialized clojure data and request-id is
                   ;; a string that is sometimes logged in the backend. Same handlers are available as with :notify,
                   ;; but with :on- prefix
                   :on-success     [::my-rf-event my-hardcoded-param]
                   :on-...         [...]
                   :on-bad-gateway [...]

                   ;; on server-error handles all 500+ response codes, if no specific handler was defined
                   :on-server-error [...]

                   ;; on-done is a special catch-all for responses where an actual handler was not defined
                   :on-done          [...]

                   ;; on-error handles connection errors, ie when backend cannot be reached
                   :on-error         [...]

                   ;; optional AbortSignal passed to the Fetch API; aborting is treated as an
                   ;; intentional cancellation (no error toast). Cancel by passing the corresponding
                   ;; AbortController to the :comm/abort effect.
                   :signal           abort-signal

                   ;; another event that always gets called with a response, in case you want to do something
                   ;; on both success and error responses
                   :finally-dispatch [...]}}

      Also,
       * generally, errors automatically result in a message to the user, while success notifications need to
         be explicitly defined (and they should)
       * for 400 Bad request, if the response contains :error :some-tr-string, that gets included in the error
         notification
       * 403 Forbidden has special handling that redirects the user to the landing page
       * 401 Unauthorized has special handling that takes the user to the login page"
  (:require ["@microsoft/fetch-event-source" :as fes]
            [clojure.string :as str]
            [core.front.operation-registry :as operation-registry]
            [core.front.routing :as routing]
            [core.front.transit :as transit-util]
            [core.http]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]))

;;
;; Query params encoding:
;;

(defn ->query-parameter [v]
  (-> (cond
        (inst? v) (.getTime v)
        ; Keywords have to be passed as strings to be interpreted correctly in back-end
        (keyword? v) (->> [(namespace v) (name v)]
                          (remove nil?)
                          (str/join "/"))
        :else v)
      js/encodeURIComponent))


(defn query-encode-kv [[k v]]
  (let [prefix (str (-> k name js/encodeURIComponent) "=")]
    (str prefix
         (if (sequential? v)
           (->> v
                (map ->query-parameter)
                (str/join (str "&" prefix)))
           (-> v ->query-parameter)))))

(defn encode-query-params [params]
  (when (seq params)
    (->> params
         (map query-encode-kv)
         (str/join "&")
         (str "?"))))

;;
;; Response handling:
;;

(defn- response-body [response body]
  (if (some-> response
              .-headers
              (.get "content-type")
              (str/starts-with? "application/transit+json"))
    (transit-util/decode body)
    body))

(defn- response-status [response]
  (aget response "status"))

(defn- request-id [response]
  (-> response
      .-headers
      (.get core.http/request-id-header)))

;;
;; Mapping from HTTP status codes to event keys:
;;

(def http-status->handler-key {200 :on-success
                               304 :on-not-modified
                               400 :on-bad-request
                               401 :on-unauthorized
                               403 :on-forbidden
                               404 :on-not-found
                               408 :on-request-timeout
                               410 :on-gone
                               412 :on-precondition-failed
                               420 :on-enhance-your-calm
                               423 :on-locked
                               500 :on-internal-server-error
                               502 :on-bad-gateway})

(def http-status->error-title
  {401 :error/on-unauthorized
   502 :error/bad-gateway
   503 :error/service-unavailable})

(def http-status->error-description
  {401 :error/on-unauthorized-description
   502 :error/bad-gateway-description
   503 :error/service-unavailable-desc})

(def http-status->toast-kind
  {502 :warning
   503 :warning})

(defn handler-key->notify-key [kw]
  (when kw
    (keyword (str/replace (name kw) #"^on-" ""))))

;;
;; HTTP options:
;;

(def default-opts {:mode        "cors"
                   :credentials "same-origin"
                   :headers     {"Content-Type" "application/transit+json"
                                 "Accept"       "application/transit+json"}})

;; Internal key threaded through the HTTP opts of monitored commands. Carries the
;; client operation id that settles the invocation in the shared operation registry;
;; plain commands never carry it and are never settled.
(defn- settle-monitored-operation! [opts]
  (when-let [operation-id (::monitor-operation-id opts)]
    (rf/dispatch [:comm/settle-operation operation-id])))

(defn error-notification-evt [status body request-id]
  [:notifications/add
   :toast
   (get http-status->toast-kind status :error)
   (or (:error-title body) (get http-status->error-title status :error/error))
   (or (:error body) (get http-status->error-description status :error-with-req-id/unknown))
   request-id])

(defn response-handler [opts [status body request-id]]
  (let [message-key (-> status
                        http-status->handler-key
                        handler-key->notify-key)
        message     (get-in opts [:notify message-key])
        kind        (if (#{:success :not-modified} message-key)
                      :success
                      :error)]
    (when message
      (rf/dispatch [:notifications/add
                    :toast
                    kind
                    (case kind
                      :error :error/error
                      kind)
                    message
                    request-id])))

  (if-let [event (or (-> status http-status->handler-key opts)
                     (when (>= status 500)
                       (-> :on-server-error opts))
                     (and (not= status 401)
                          (-> :on-done opts)))]
    (rf/dispatch (conj event status body request-id))

    (cond
      (= status 403)
      (rf/dispatch [::routing/reset-view])

      (= status 401)
      (rf/dispatch [:session/session-expired])

      (>= status 400)
      (rf/dispatch (error-notification-evt status body request-id))))

  (when-let [event (-> opts :finally-dispatch)]
    (rf/dispatch (conj event status body)))

  (settle-monitored-operation! opts))


(defn- abort-error? [exception]
  (= "AbortError" (some-> exception .-name)))

(defn error-handler [{:keys [command status request-id exception] :as opts}]
  ;; intentional aborts (AbortError) are user-driven cancellations, not failures
  (when-not (abort-error? exception)
    (if-let [event (:on-error opts)]
      (rf/dispatch (conj event status))
      (do
        (js/console.error "unexpected error while executing command" command status)
        (rf/dispatch [:notifications/add
                      :toast
                      :error
                      ; fetch will throw TypeError if backend connection fails
                      (if (= (type exception) js/TypeError)
                        :error/connection-error-title
                        :error/error)
                      (if (= (type exception) js/TypeError)
                        :error/connection-error-description
                        :error-with-req-id/unknown)
                      request-id]))))
  (settle-monitored-operation! opts))

;;
;; Wrap Fetch API:
;;

(defn fetch [{:keys [method url headers data signal] :or {method :post}}]
  (when ^boolean js/goog.DEBUG
    (js/console.debug "fetch:" (-> method name str/upper-case) url (clj->js data)))
  (js/window.fetch
    (str url (when (= method :get)
               (encode-query-params data)))
    (-> default-opts
        (assoc :method (case method
                         :post "POST"
                         :get "GET"))
        (assoc :body (when (= method :post)
                       (transit-util/encode data)))
        (p/assoc-if :signal signal)
        (update :headers merge headers)
        (update :headers merge {core.http/request-id-header (random-uuid)})
        clj->js)))

;;
;; Send command execution over HTTP:
;;

(defn fetch-command [opts]
  (-> (fetch opts)
      (.then (fn [response]
               (js/Promise. (fn [resolve _]
                              (-> (.text response)
                                  (.then (fn [body]
                                           (resolve [(response-status response)
                                                     (response-body response body)
                                                     (request-id response)]))))))))
      (.then (partial response-handler opts))
      (.catch (fn [error]
                (error-handler (merge opts {:exception error}))))))


(defn send-command-execution [{:keys [command] :as fx-options}]
  (cond
    (map? fx-options)
    (fetch-command (assoc fx-options :url (core.http/command-name->path command)))

    (vector? fx-options)
    (doseq [opts fx-options]
      (fetch-command (assoc opts :url (core.http/command-name->path (:command opts)))))

    :else (js/console.error (str "Wrong args provided to :comm/command:" fx-options))))


(rf/reg-fx :comm/command send-command-execution)
(rf/reg-fx :comm/response (fn [{:keys [opts response]}] (response-handler (or opts {}) response)))
(rf/reg-fx :comm/error error-handler)
(rf/reg-fx :comm/abort (fn [abort-controller] (some-> abort-controller .abort)))
(rf/reg-fx :comm/dev-command #(fetch-command (assoc % :url (core.http/command-name->path "/dev/" (:command %)))))
(rf/reg-event-fx :comm/noop (fn [& _]))


(rf/reg-event-db :comm/settle-operation
  (fn [db [_ operation-id]]
    (operation-registry/settle-operation db operation-id)))


(rf/reg-sub :comm/current-processing-commands
  (fn [db _]
    (->> (operation-registry/active-operations db)
         (keep (fn [[_ metadata]] (:command metadata)))
         set)))


(rf/reg-sub :comm/command-processing?
  :<- [:comm/current-processing-commands]
  (fn [current-commands [_ command]]
    (contains? current-commands command)))


(defn- command-monitored-handler
  "Tracks a command invocation in the shared operation registry.

   Callers may provide `:operation-id` to correlate an operation; otherwise a UUID is generated.
   IDs must be unique among independent in-flight commands."
  [{:keys [db]} [_ {:keys [command operation-id] :as opts}]]
  (let [operation-id (or operation-id (random-uuid))]
    {:comm/command (assoc opts ::monitor-operation-id operation-id)
     :db           (operation-registry/start-operation db operation-id {:command command})}))

(rf/reg-event-fx :comm/command-monitored command-monitored-handler)

(rf/reg-event-fx :comm/add-error-notification
  (fn [_ [_ status body request-id]]
    {:dispatch (error-notification-evt status body request-id)}))

;;
;; Server-sent events (SSE) communications:
;;


(defn build-sse-url
  "Build the SSE URL with query parameters from a command name keyword."
  [command-name query-params]
  (str (core.http/command-name->path command-name)
       (when query-params
         (encode-query-params query-params))))

(defn create-event-source
  "Create an EventSource for SSE notifications, by making a get request to the given `url`.
   Returns the EventSource instance.

   EventSource must be closed when no longer needed, because the browser will otherwise try to reconnect to it.
   When the server closes the connection, the `on-error` callback is called. If `close-on-error?` is true,
   the EventSource is closed on error; otherwise, it will try to reconnect automatically."
  [{:keys [url event-name on-message on-error close-on-error?]}]
  (let [event-source (js/EventSource. url)]
    ;; Listen for events of `event-name`
    (.addEventListener event-source event-name (fn [event]
                                                 (when-let [data (.-data event)]
                                                   (on-message (transit-util/decode data)))))
    ;; Handle errors (connection lost, etc.)
    (set! (.-onerror event-source)
          (fn [_event]
            (when on-error (on-error))
            (when close-on-error?
              (.close event-source))))
    event-source))

(defn fetch-event-source
  "Starts an EventSource-like stream for SSE notifications, by making a Fetch request to the given `url`.
   `method` can be :get or :post
   `data` can be provided and is used for query params for get and transit-encoded body for post.
   Returns a function that can be called to close the stream via an AbortController."
  [{:keys [url event-name method data on-message on-close on-error close-on-error?]}]
  (let [ctrl (js/AbortController.)]
    (fes/fetchEventSource
      (str url (when (= method :get)
                 (encode-query-params data)))
      (-> default-opts
          (assoc-in [:headers "Accept"] "text/event-stream")
          (assoc :onmessage (fn [event]
                              (when-let [data (and (or (nil? event-name)
                                                       (= event-name (.-event event)))
                                                   (.-data event))]
                                (on-message (transit-util/decode data))))
                 :onopen  (fn [response]
                            ; Should we just use (.-ok response)? Why doesn't the default handler do that? Retries?
                            (when (#(or (= % 429) (<= 500 %)) (.-status response))
                              (let [err (js/Error. (str "HTTP " (.-status response)))]
                                (set! (.-response err) response)
                                (throw err)))
                            ; From the default handler: https://github.com/Azure/fetch-event-source/blob/45ac3cfffd30b05b79fbf95c21e67d4ef59aa56a/src/fetch.ts#L149-L154
                            (let [ctype (-> response .-headers (.get "content-type"))]
                              (when-not (some-> ctype (.startsWith "text/event-stream"))
                                (throw (js/Error. (str "Expected content-type to be text/event-stream, Actual: " ctype))))))
                 :onclose (fn []
                            (when on-close (on-close)))
                 :onerror (fn [err]
                            (when on-error (on-error err))
                            (when close-on-error?
                              (throw err)))
                 :method (case method
                           :post "POST"
                           :get "GET")
                 :openWhenHidden true
                 :signal (.-signal ctrl))
          (p/assoc-if :body (when (and (= method :post) data)
                              (transit-util/encode data)))
          clj->js))
    #(.abort ctrl)))
